import type { AuditUser } from '../../api/monitor';
import { SearchSelect } from '../../components/ui/SearchSelect';
import { RANGE_PRESETS } from '../../lib/audit';
import type { AuditRange, RangePresetId } from '../../lib/audit';
import { DateRangePicker } from './DateRangePicker';

interface AuditFiltersProps {
  users: AuditUser[];
  /** `null` = todos. */
  userId: number | null;
  range: AuditRange;
  preset: RangePresetId | null;
  filtered: boolean;
  onUserChange: (id: number | null) => void;
  onRangeChange: (range: AuditRange) => void;
  onPreset: (preset: RangePresetId, days: number) => void;
  onClear: () => void;
}

/**
 * Os filtros da auditoria: quem e quando.
 *
 * Os atalhos de período ficam abaixo do campo, e não dentro do calendário,
 * porque "últimos 7 dias" é o recorte que se pede na maioria das vezes — pedir
 * dois cliques para chegar nele deixaria o caso comum mais caro que o raro.
 */
export function AuditFilters({
  users,
  userId,
  range,
  preset,
  filtered,
  onUserChange,
  onRangeChange,
  onPreset,
  onClear,
}: AuditFiltersProps) {
  return (
    <div className="border-line-200 mb-3 rounded-[14px] border bg-white p-3.5">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2.5">
        <label className="text-ink-600 flex flex-col gap-1.5 text-[11.5px] font-bold">
          Usuário
          <SearchSelect
            value={userId === null ? '' : String(userId)}
            onChange={(value) => onUserChange(Number(value) || null)}
            options={users.map((user) => ({ value: String(user.id), label: user.name }))}
            clearable
            placeholder="Todos os usuários"
            searchPlaceholder="Buscar usuário…"
          />
        </label>

        <DateRangePicker range={range} onChange={onRangeChange} />
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {RANGE_PRESETS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onPreset(option.id, option.days)}
            aria-pressed={preset === option.id}
            className={`rounded-full border px-3.5 py-1.5 text-[12px] font-bold ${
              preset === option.id
                ? 'border-navy-800 bg-navy-800 text-white'
                : 'border-line-300 text-ink-600 bg-white'
            }`}
          >
            {option.label}
          </button>
        ))}

        <div className="flex-1" />

        {filtered ? (
          <button
            type="button"
            onClick={onClear}
            className="text-navy-800 text-[12px] font-extrabold"
          >
            Limpar filtros
          </button>
        ) : null}
      </div>
    </div>
  );
}
